'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, MessageSquare, User, Sparkles } from 'lucide-react';
import GradientText from '@/components/ui/GradientText';
import Hyperspeed from '@/components/ui/Hyperspeed';
import { hyperspeedPresets } from '@/components/ui/hyperspeed-presets';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
const inputVariants = {
  focus: {
    scale: 1.02,
    borderColor: "rgba(var(--primary), 0.8)",
    boxShadow: "0 0 15px rgba(var(--primary), 0.2)"
  },
  blur: {
    scale: 1,
    borderColor: "rgba(255, 255, 255, 0.1)",
    boxShadow: "none"
  }
};
export default function ContactPage() {
  const $ = _c(42);
  const [focusedField, setFocusedField] = useState(null);
  let baseOptions;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const {
      onSpeedUp,
      onSlowDown,
      ...t0
    } = hyperspeedPresets.five as any;
    baseOptions = t0;
    $[0] = baseOptions;
  } else {
    baseOptions = $[0];
  }
  let t0;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...baseOptions,
      colors: {
        roadColor: 328965,
        islandColor: 657930,
        background: 0,
        shoulderLines: 16777215,
        brokenLines: 16777215,
        leftCars: [16711765, 16724855, 16711765],
        rightCars: [61183, 3405311, 61183],
        sticks: 61183
      }
    };
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const contactPreset = t0 as any;
  let t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="absolute inset-0 z-0"><Hyperspeed effectOptions={contactPreset} /></div>;
    t2 = <div className="absolute inset-0 bg-background/50 backdrop-blur-[2px] z-0" />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0,
      x: -50
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      hidden: t3,
      visible: {
        opacity: 1,
        x: 0,
        transition: {
          staggerChildren: 0.1
        }
      }
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <motion.div variants={fadeInUp} className="mb-6"><span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest border border-primary/20 backdrop-blur-md"><Sparkles className="w-3 h-3 mr-2" /> Get in Touch</span></motion.div>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = ["#FF0055", "#00EEFF", "#FF0055", "#00EEFF", "#FF0055"];
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <motion.div variants={fadeInUp} className="relative mb-8 pb-2"><GradientText colors={t6} animationSpeed={5} className="text-5xl md:text-7xl font-bold tracking-tight text-left leading-[1.1]" showBorder={false}>Let's Build the <br /> Future Together</GradientText></motion.div>;
    t8 = <motion.p variants={fadeInUp} className="text-xl text-muted-foreground/90 leading-relaxed max-w-md">Have a project in mind or just want to discuss the latest in AI and Web Tech? I'm always open to new ideas and collaborations.</motion.p>;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Email</span>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <motion.div initial="hidden" animate="visible" variants={t4}>{t5}{t7}{t8}<motion.div variants={fadeInUp} className="mt-12 flex gap-6"><div className="flex flex-col gap-2">{t9}<a href="mailto:lalitchoudhary112000@gmail.com" className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2"><Mail className="w-4 h-4" /> lalitchoudhary112000@gmail.com</a></div></motion.div></motion.div>;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  let t12;
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      opacity: 0,
      scale: 0.9,
      y: 50
    };
    t12 = {
      opacity: 1,
      scale: 1,
      y: 0
    };
    t13 = {
      delay: 0.2,
      duration: 0.5
    };
    $[12] = t11;
    $[13] = t12;
    $[14] = t13;
  } else {
    t11 = $[12];
    t12 = $[13];
    t13 = $[14];
  }
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="absolute -inset-1 bg-gradient-to-r from-primary via-accent-purple to-primary rounded-3xl blur opacity-30 animate-pulse" />;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  const t15 = focusedField === "name" ? "focus" : "blur";
  let t16;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <label htmlFor="name" className="text-sm font-medium ml-1 flex items-center gap-2 text-muted-foreground"><User className="w-3 h-3" /> Name</label>;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <motion.input type="text" id="name" variants={inputVariants} onFocus={() => setFocusedField("name")} onBlur={() => setFocusedField(null)} className="flex h-12 w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-sm placeholder:text-muted-foreground/50 focus:outline-none transition-all duration-300" placeholder="Jane Doe" />;
    $[17] = t17;
  } else {
    t17 = $[17];
  }
  let t18;
  if ($[18] !== t15) {
    t18 = <motion.div className="space-y-2" animate={t15}>{t16}{t17}</motion.div>;
    $[18] = t15;
    $[19] = t18;
  } else {
    t18 = $[19];
  }
  const t19 = focusedField === "email" ? "focus" : "blur";
  let t20;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <label htmlFor="email" className="text-sm font-medium ml-1 flex items-center gap-2 text-muted-foreground"><Mail className="w-3 h-3" /> Email</label>;
    $[20] = t20;
  } else {
    t20 = $[20];
  }
  let t21;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <motion.input type="email" id="email" variants={inputVariants} onFocus={() => setFocusedField("email")} onBlur={() => setFocusedField(null)} className="flex h-12 w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-sm placeholder:text-muted-foreground/50 focus:outline-none transition-all duration-300" placeholder="jane@example.com" />;
    $[21] = t21;
  } else {
    t21 = $[21];
  }
  let t22;
  if ($[22] !== t19) {
    t22 = <motion.div className="space-y-2" animate={t19}>{t20}{t21}</motion.div>;
    $[22] = t19;
    $[23] = t22;
  } else {
    t22 = $[23];
  }
  let t23;
  if ($[24] !== t18 || $[25] !== t22) {
    t23 = <div className="grid md:grid-cols-2 gap-6">{t18}{t22}</div>;
    $[24] = t18;
    $[25] = t22;
    $[26] = t23;
  } else {
    t23 = $[26];
  }
  const t24 = focusedField === "subject" ? "focus" : "blur";
  let t25;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <label htmlFor="subject" className="text-sm font-medium ml-1 flex items-center gap-2 text-muted-foreground"><MessageSquare className="w-3 h-3" /> Subject</label>;
    $[27] = t25;
  } else {
    t25 = $[27];
  }
  let t26;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <motion.input type="text" id="subject" variants={inputVariants} onFocus={() => setFocusedField("subject")} onBlur={() => setFocusedField(null)} className="flex h-12 w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-sm placeholder:text-muted-foreground/50 focus:outline-none transition-all duration-300" placeholder="Project Collaboration" />;
    $[28] = t26;
  } else {
    t26 = $[28];
  }
  let t27;
  if ($[29] !== t24) {
    t27 = <motion.div className="space-y-2" animate={t24}>{t25}{t26}</motion.div>;
    $[29] = t24;
    $[30] = t27;
  } else {
    t27 = $[30];
  }
  const t28 = focusedField === "message" ? "focus" : "blur";
  let t29;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <label htmlFor="message" className="text-sm font-medium ml-1 flex items-center gap-2 text-muted-foreground"><MessageSquare className="w-3 h-3" /> Message</label>;
    $[31] = t29;
  } else {
    t29 = $[31];
  }
  let t30;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <motion.textarea id="message" variants={inputVariants} onFocus={() => setFocusedField("message")} onBlur={() => setFocusedField(null)} className="flex min-h-[150px] w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm placeholder:text-muted-foreground/50 focus:outline-none transition-all duration-300 resize-none" placeholder="I'd like to talk about..." />;
    $[32] = t30;
  } else {
    t30 = $[32];
  }
  let t31;
  if ($[33] !== t28) {
    t31 = <motion.div className="space-y-2" animate={t28}>{t29}{t30}</motion.div>;
    $[33] = t28;
    $[34] = t31;
  } else {
    t31 = $[34];
  }
  let t32;
  let t33;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      scale: 1.02
    };
    t33 = {
      scale: 0.98
    };
    $[35] = t32;
    $[36] = t33;
  } else {
    t32 = $[35];
    t33 = $[36];
  }
  let t34;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <motion.button type="submit" whileHover={t32} whileTap={t33} className="w-full inline-flex items-center justify-center rounded-xl text-sm font-bold transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 bg-gradient-to-r from-primary to-accent-purple text-primary-foreground h-12 px-8 shadow-lg shadow-primary/20 hover:shadow-primary/40"><Send className="w-4 h-4 mr-2" /> Send Message</motion.button>;
    $[37] = t34;
  } else {
    t34 = $[37];
  }
  let t35;
  if ($[38] !== t23 || $[39] !== t27 || $[40] !== t31) {
    t35 = <div className="min-h-screen relative overflow-hidden bg-background text-foreground flex items-center justify-center py-20">{t1}{t2}<div className="container relative z-10 max-w-7xl px-4"><div className="grid lg:grid-cols-2 gap-20 items-center">{t10}<motion.div initial={t11} animate={t12} transition={t13} className="relative lg:ml-auto w-full max-w-lg">{t14}<div className="relative bg-card/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-10 shadow-2xl"><form className="space-y-6">{t23}{t27}{t31}{t34}</form></div></motion.div></div></div></div>;
    $[38] = t23;
    $[39] = t27;
    $[40] = t31;
    $[41] = t35;
  } else {
    t35 = $[41];
  }
  return t35;
}